import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { SelectButtonItem } from "./models/select-button-item";
import { Nillable } from "../../types/nillable";
import { isNil } from "lodash-es";
import { SelectButtonOptionClickEvent } from "./events/select-button-option-click-event";

@Component({
  selector: "select-button",
  templateUrl: "./select-button.component.html",
  styleUrls: ["./select-button.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectButtonComponent<Value> implements OnChanges, OnInit {
  @Output() optionClickEvent =
    new EventEmitter<SelectButtonOptionClickEvent<Value> | null>();

  @Input() unselectable: boolean = true;
  @Input() selected: Nillable<Value | Value[]>;
  @Input() options: Array<SelectButtonItem<Value>> = [];
  @Input() readonly: boolean = false;
  @Input() multiple: boolean = false;
  @Input() selectAllOnInit: boolean = false;
  @Input() variant: "default" | "segment" = "default";

  value: Nillable<Value>;
  values: Value[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    const { selected, options, multiple, selectAllOnInit } = changes;

    if (selected || options || multiple || selectAllOnInit) {
      this.configureSelectedOptions();
    }
  }

  ngOnInit(): void {
    this.configureSelectedOptions();

    if (!this.unselectable && !this.multiple && isNil(this.selected)) {
      this.value = null;
    }
  }

  isSelected(option: SelectButtonItem<Value>): boolean {
    if (this.multiple) {
      return this.values.includes(option.value);
    }

    return this.value === option.value;
  }

  handleOptionSelect(
    event: MouseEvent,
    option: SelectButtonItem<Value>,
    index: number,
  ): void {
    if (this.readonly) {
      return;
    }

    const isSelected = this.isSelected(option);

    if (this.multiple) {
      const model = new SelectButtonOptionClickEvent(event, option, index);

      if (isSelected) {
        if (this.unselectable && this.values.length <= 1) {
          return;
        }

        this.values = this.values.filter((value) => value !== option.value);
        this.optionClickEvent.emit(model);
        return;
      }

      this.values = [...this.values, option.value];
      this.optionClickEvent.emit(model);
      return;
    }

    if (isSelected && !this.unselectable) {
      this.value = null;
      this.optionClickEvent.emit(null);
      return;
    }

    if (isSelected && this.unselectable) {
      return;
    }

    const model = new SelectButtonOptionClickEvent(event, option, index);

    if (option.value === this.value) {
      this.value = null;
      this.optionClickEvent.emit(model);

      return;
    }
    this.value = option.value;
    this.optionClickEvent.emit(model);
  }

  private configureSelectedOptions(): void {
    if (this.multiple) {
      this.configureMultipleSelectedOptions();
      return;
    }

    const found = this.options.find((option) => option.value === this.selected);

    if (isNil(found)) {
      this.value = this.unselectable ? this.options[0]?.value : null;

      return;
    }

    this.value = found.value;
  }

  private configureMultipleSelectedOptions(): void {
    if (Array.isArray(this.selected)) {
      const selectedValues = this.selected;
      this.values = this.options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => option.value);
      return;
    }

    if (!isNil(this.selected)) {
      this.values = [this.selected];
      return;
    }

    if (this.selectAllOnInit) {
      this.values = this.options.map((option) => option.value);
      return;
    }

    this.values = [];
  }
}
